/**
 * Break Management KPI Strip — Concept C (Live Command Center)
 * ------------------------------------------------------------
 * Four KPI cards arranged to mirror their operational urgency:
 *
 *   1. Active Breaks  — emerald gradient, LIVE tag, utilization bar
 *   2. Flagged Open   — rose gradient, LIVE tag, severity breakdown
 *   3. Today's Breaks — outline card, TitleFlow blue/purple stripe
 *   4. Force-Ended Today — outline card, amber stripe
 *
 * Optional `onPoll` callback (no current consumer) so there is
 * exactly one polling owner across the page (no drift between the
 * "last updated" timestamp and the rendered numbers).
 *
 * Click-through map (per user spec):
 *   • Active     → /hr/breaks/active
 *   • Flagged    → /hr/breaks/flagged
 *   • Today      → /hr/breaks/history?date_from=<today>&date_to=<today>
 *   • Force-Ended → /hr/breaks/history?date_from=<today>&date_to=<today>&status=force_ended
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Coffee, Flag, CalendarClock, XCircle } from 'lucide-react';
import { BreakKpiCard, BreakErrorState } from './ui/BreakUi';

const API = process.env.REACT_APP_BACKEND_URL || '';

const numberFmt = (n) => (n ?? 0).toLocaleString('en-US');

// Today's ISO date (YYYY-MM-DD) for URL filters.
const todayIso = () => new Date().toISOString().slice(0, 10);

// ── Main strip ──────────────────────────────────────────────────
const BreakKpiStrip = ({ onPoll }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchKpis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/breaks/kpi-summary`);
      setData(res.data);
      setError(null);
      // Notify the LiveOperationsBar so it can update its timestamp
      // and scope label without a second network round-trip.
      if (onPoll) onPoll({ at: new Date(), scope: res.data.scope, scopeUserCount: res.data.scope_user_count });
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, [onPoll]);

  useEffect(() => {
    fetchKpis();
    const id = setInterval(fetchKpis, 30000);
    return () => clearInterval(id);
  }, [fetchKpis]);

  if (error) {
    return (
      <div className="mb-5"><BreakErrorState title="Couldn't load break KPIs." detail={error} onRetry={fetchKpis} testId="kpi-strip-error" /></div>
    );
  }

  const today = todayIso();

  // Active utilization bar — what % of in-scope users are currently
  // on break. Only shown when the server reports a scope user count.
  const activeUtilPct = (() => {
    if (!data?.scope_user_count || data.scope === 'company') return null;
    if (!data?.active_breaks) return 0;
    return Math.min(100, Math.round((data.active_breaks / data.scope_user_count) * 100));
  })();

  return (
    <div className="mb-6" data-testid="break-kpi-strip">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">

        <BreakKpiCard testid="kpi-active" icon={Coffee} tone="green" label="Active Breaks" value={data?.active_breaks} loading={loading} delay={0}
          onClick={() => navigate('/hr/breaks/active')}
          sub={
            <div className="mt-1.5" data-testid="kpi-active-sub">
              <div className="h-1 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${activeUtilPct ?? 0}%` }} /></div>
              <div className="text-slate-400 text-[10.5px] mt-1 leading-tight flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" style={{ animation: 'liveops-pulse 1.6s infinite' }} />
                {activeUtilPct != null ? `${activeUtilPct}% of in-scope team on break` : (data?.active_breaks ? 'Live · currently on break' : 'Live · no one on break right now')}
              </div>
            </div>
          }
        />
        <BreakKpiCard testid="kpi-flagged" icon={Flag} tone="red" label="Flagged · Open" value={data?.flagged_breaks} loading={loading} delay={60} emphasis={!!data?.flagged_breaks}
          onClick={() => navigate('/hr/breaks/flagged')}
          sub={<div className="text-slate-400 text-[10.5px] mt-1 leading-tight">{data?.flagged_breaks ? 'Pending review' : 'Queue is clear'}</div>}
        />
        <BreakKpiCard testid="kpi-today" icon={CalendarClock} tone="blue" label="Today's Breaks" value={data?.todays_breaks} loading={loading} delay={120}
          onClick={() => navigate(`/hr/breaks/history?date_from=${today}&date_to=${today}`)}
          sub={
            <div className="text-slate-400 text-[10.5px] mt-1 leading-tight">
              {data?.todays_minutes != null && data.todays_minutes > 0 ? <><span className="font-mono text-slate-600">{numberFmt(data.todays_minutes)}m</span> used today</> : 'Started since midnight ET'}
            </div>
          }
        />
        <BreakKpiCard testid="kpi-force-ended" icon={XCircle} tone="amber" label="Force-Ended Today" value={data?.force_ended_today} loading={loading} delay={180}
          onClick={() => navigate(`/hr/breaks/history?date_from=${today}&date_to=${today}&status=force_ended`)}
          sub={<div className="text-slate-400 text-[10.5px] mt-1 leading-tight">{data?.force_ended_today ? 'Admin overrides today' : 'No overrides today'}</div>}
        />
      </div>
    </div>
  );
};

export default BreakKpiStrip;
